import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, ChevronUp, Columns3, Filter, Search, X } from 'lucide-react';
import {
  flattenHomePages,
  totalsFromHomePages,
  useHomeFacets,
  useInfiniteHomes,
} from '../../api/homes-hooks.js';
import type { HomeRow, HomeTotals } from '../../api/client.js';
import { PanelFrame } from '../panel-frame.js';
import { ColumnsMenu } from '../columns-menu.js';
import { ColumnHeaderCell } from '../column-header-cell.js';
import { HomesFilterModal } from '../homes-filter-modal.js';
import { HomeRowContextMenu } from '../home-row-context-menu.js';
import { homeSelection, useOpenHomeRecord, type HomeTarget } from '../use-open-home-record.js';
import {
  buildHomesFilterQuery,
  countActiveHomeFilters,
  defaultHomeCategoricals,
  defaultHomeRanges,
  type HomeCategoricalKey,
  type HomeCategoricals,
  type HomeRange,
  type HomeRangeKey,
  type HomeRanges,
} from '../homes-filters.js';
import {
  HOMES_COLUMN_DEFS,
  homesRequestColumns,
  renderHomeCell,
  useHomesColumnConfig,
  type HomesColumnActions,
  type HomesColumnConfig,
  type HomesColumnId,
} from '../homes-columns.js';
import { useHomesFilterLink } from '../homes-filter-link.js';
import {
  usePanel,
  usePanelSelection,
  useSetPanelSelection,
  type PanelId,
  type SelectionKind,
  type TabId,
} from '../store.js';

// Homes — the care-home screener (TERMINAL_HOME_GRAIN_SPEC step 3). One row per
// in-scope care place across the 27 markets, from GET /homes (care_home_index).
// The Triage table kit, at home grain:
//   - frozen Name pane + scrollable, resizable / reorderable / hideable columns
//     (homes-columns.ts → ColumnsMenu / ColumnHeaderCell), persisted per panel;
//   - server-side sort and filters (homes-filters.ts → HomesFilterModal), only
//     the visible columns requested;
//   - TanStack Virtual rows + an IntersectionObserver sentinel for paging;
//   - j/k / arrows, g/G, / to search.
// Clicking a row broadcasts a `kind: 'home'` selection — a linked Home record
// follows it, and so does a linked operator Record (store deriveEntityId moves
// currentEntityId to the home's operator). Broadcast-only, like Triage: a click
// elsewhere never reaches back into this list.

const ROW_HEIGHT = 26;
const COLUMN_CONFIG_KEY = 'corryk:homes-columns:v2';
const ACCEPTS: readonly SelectionKind[] = ['home'];

type SortKey = 'name' | HomesColumnId;

type LifecycleChoice = 'operating' | 'development' | 'all';

export function HomesPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const [columnConfig, colActions] = useHomesColumnConfig(COLUMN_CONFIG_KEY);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);

  const [categoricals, setCategoricals] = useState<HomeCategoricals>(defaultHomeCategoricals);
  const [ranges, setRanges] = useState<HomeRanges>(defaultHomeRanges);
  const [has, setHas] = useState<ReadonlySet<string>>(() => new Set());
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('beds');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(query), 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const setCategorical = (key: HomeCategoricalKey, next: Set<string>) =>
    setCategoricals((prev) => ({ ...prev, [key]: next }));
  const setRange = (key: HomeRangeKey, next: HomeRange) =>
    setRanges((prev) => ({ ...prev, [key]: next }));
  const clearAll = () => {
    setCategoricals(defaultHomeCategoricals());
    setRanges(defaultHomeRanges());
    setHas(new Set());
    setQuery('');
  };

  const lifecycle: LifecycleChoice =
    categoricals.lifecycle.size === 1
      ? categoricals.lifecycle.has('development')
        ? 'development'
        : 'operating'
      : 'all';
  const setLifecycle = (v: LifecycleChoice) =>
    setCategorical('lifecycle', v === 'all' ? new Set() : new Set([v]));

  const activeFilterCount = countActiveHomeFilters(categoricals, ranges, has, debouncedQ);

  // Debounced like the search box: dragging a range slider moves through many
  // values, and each one must not become its own /homes + /homes/facets request.
  const liveFilterQuery = useMemo(
    () => buildHomesFilterQuery(categoricals, ranges, has, debouncedQ),
    [categoricals, ranges, has, debouncedQ],
  );
  const [filterQuery, setFilterQuery] = useState(liveFilterQuery);
  useEffect(() => {
    const id = window.setTimeout(() => setFilterQuery(liveFilterQuery), 250);
    return () => window.clearTimeout(id);
  }, [liveFilterQuery]);

  // Publish the filters to this panel's channel, so a linked Map plots the same homes.
  const panel = usePanel(tabId, panelId);
  const linkGroupId = panel?.link.kind === 'linked' ? panel.link.groupId : null;
  const setChannelFilters = useHomesFilterLink((s) => s.setFor);
  useEffect(() => {
    if (!linkGroupId) return;
    const key = `${tabId}:${linkGroupId}`;
    setChannelFilters(key, filterQuery);
    return () => setChannelFilters(key, null);
  }, [tabId, linkGroupId, filterQuery, setChannelFilters]);
  const requestColumns = useMemo(
    () => homesRequestColumns(columnConfig.columns),
    [columnConfig.columns],
  );
  const listQuery = useMemo(
    () => ({ ...filterQuery, columns: requestColumns, sort: sortKey, dir: sortDir }),
    [filterQuery, requestColumns, sortKey, sortDir],
  );

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteHomes(listQuery);
  const facetsQ = useHomeFacets(filterQuery, filtersOpen);
  const rows = useMemo(() => flattenHomePages(data), [data]);
  const totals = totalsFromHomePages(data);

  // Row selection: the latest home this panel broadcast (its local selection).
  const selection = usePanelSelection(tabId, panelId, ACCEPTS);
  const selectedKey = selection?.key ?? null;
  const setPanelSelection = useSetPanelSelection(tabId, panelId);
  const select = (row: HomeRow) => setPanelSelection(homeSelection(targetOf(row)));
  const openHomeRecord = useOpenHomeRecord();

  const panelRootRef = useRef<HTMLDivElement>(null);
  function onPanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (rows.length === 0) return;
    const idx = selectedKey ? rows.findIndex((r) => r.home_key === selectedKey) : -1;
    const go = (i: number) => {
      const row = rows[Math.max(0, Math.min(rows.length - 1, i))];
      if (row) select(row);
    };
    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      go(idx === -1 ? 0 : idx + 1);
    } else if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      go(idx === -1 ? 0 : idx - 1);
    } else if (e.key === 'g') {
      e.preventDefault();
      go(0);
    } else if (e.key === 'G') {
      e.preventDefault();
      go(rows.length - 1);
    } else if (e.key === '/') {
      e.preventDefault();
      panelRootRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
    }
  }

  function onSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir(k !== 'name' && HOMES_COLUMN_DEFS[k].numeric ? 'desc' : 'asc');
    }
  }

  return (
    <PanelFrame tabId={tabId} panelId={panelId} title="Homes">
      <div
        ref={panelRootRef}
        tabIndex={0}
        onKeyDown={onPanelKeyDown}
        style={{
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr',
          height: '100%',
          minHeight: 0,
          outline: 'none',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            padding: 'var(--s-2) var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            background: 'var(--surface)',
          }}
        >
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              fontSize: 'var(--t-body)',
            }}
          >
            <span style={{ color: 'var(--text-secondary)' }}>Lifecycle</span>
            <select
              value={lifecycle}
              onChange={(e) => setLifecycle(e.target.value as LifecycleChoice)}
              aria-label="Lifecycle"
            >
              <option value="operating">Operating</option>
              <option value="development">In development</option>
              <option value="all">All</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-haspopup="dialog"
            style={toolbarBtn(activeFilterCount > 0)}
          >
            <Filter size={12} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span
                data-numeric
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--t-meta)',
                  color: 'var(--accent)',
                }}
              >
                · {activeFilterCount}
              </span>
            )}
          </button>
          <button
            ref={columnsBtnRef}
            type="button"
            onClick={() => setColumnsOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={columnsOpen}
            title="Show, hide and reorder columns"
            style={toolbarBtn(columnsOpen)}
          >
            <Columns3 size={12} />
            <span>Columns</span>
          </button>
          <ColumnsMenu
            open={columnsOpen}
            onClose={() => setColumnsOpen(false)}
            anchorRef={columnsBtnRef}
            columns={columnConfig.columns}
            label={(id) => HOMES_COLUMN_DEFS[id].label}
            actions={colActions}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-1)',
              flex: 1,
              padding: '2px var(--s-2)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              background: 'var(--menu-surface)',
              maxWidth: 360,
            }}
          >
            <Search size={12} style={{ opacity: 0.55 }} />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Home, address, postcode or operator…"
              aria-label="Search homes"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 'var(--t-body)',
                color: 'var(--text-primary)',
              }}
            />
            {query && (
              <X
                size={10}
                onClick={() => setQuery('')}
                style={{ cursor: 'pointer', opacity: 0.6 }}
              />
            )}
          </div>
          <div style={{ flex: 1 }} />
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearAll}
              style={{
                fontSize: 'var(--t-meta)',
                padding: '2px var(--s-1)',
                border: '1px solid var(--hairline)',
                borderRadius: 2,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </div>

        <TotalsStrip totals={totals} />

        <HomesFilterModal
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          categoricals={categoricals}
          setCategorical={setCategorical}
          ranges={ranges}
          setRange={setRange}
          has={has}
          setHas={setHas}
          facets={facetsQ.data ?? null}
          onClearAll={clearAll}
        />

        <div
          style={{
            padding: 'var(--s-3)',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <HomesTable
            columnConfig={columnConfig}
            colActions={colActions}
            rows={rows}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
            selectedKey={selectedKey}
            onSelect={select}
            onOpen={(row) => openHomeRecord(targetOf(row))}
            isLoading={isLoading}
            error={!!error}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => {
              if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
            }}
            developmentOnly={lifecycle === 'development'}
          />
        </div>
      </div>
    </PanelFrame>
  );
}

function targetOf(row: HomeRow): HomeTarget {
  return {
    homeKey: row.home_key,
    name: typeof row['name'] === 'string' ? row['name'] : null,
    operatorEntityId:
      typeof row['operator_entity_id'] === 'string' ? row['operator_entity_id'] : null,
  };
}

function toolbarBtn(on: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--s-1)',
    padding: '2px var(--s-2)',
    fontSize: 'var(--t-body)',
    border: '1px solid var(--hairline)',
    borderRadius: 2,
    background: on ? 'var(--color-accent-100)' : 'var(--surface)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    flexShrink: 0,
  };
}

// --- totals --------------------------------------------------------------

/** Totals rule: homes AND beds AND distinct operators / groups / landlords,
 *  side by side — never one alone. Planned beds appear only when the filter
 *  admits homes in development, and never summed into operating beds. */
function TotalsStrip({ totals }: { totals: HomeTotals | null }) {
  const items: Array<{ label: string; value: number | null; title?: string }> = [
    { label: 'homes', value: totals?.homes ?? null },
    {
      label: 'beds',
      value: totals?.beds_operating ?? null,
      title: totals
        ? `Published operating beds — ${totals.homes_with_beds.toLocaleString('en-GB')} of ${totals.homes.toLocaleString('en-GB')} homes publish a figure`
        : undefined,
    },
    ...(totals && totals.beds_planned > 0
      ? [{ label: 'planned beds', value: totals.beds_planned }]
      : []),
    {
      label: 'operators',
      value: totals?.operators ?? null,
      title: 'Distinct linked operator companies (in care scope)',
    },
    { label: 'groups', value: totals?.groups ?? null },
    { label: 'landlords', value: totals?.landlords ?? null },
  ];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 'var(--s-4)',
        flexWrap: 'wrap',
        padding: '6px var(--s-3)',
        borderBottom: '1px solid var(--hairline)',
        background: 'var(--surface)',
        fontSize: 'var(--t-meta)',
      }}
    >
      {items.map((it) => (
        <span
          key={it.label}
          title={it.title}
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}
        >
          <span
            data-numeric
            style={{
              fontFamily: 'var(--font-mono)',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 'var(--t-body)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {it.value === null ? '—' : Math.round(it.value).toLocaleString('en-GB')}
          </span>
          <span style={{ color: 'var(--text-tertiary)' }}>{it.label}</span>
        </span>
      ))}
    </div>
  );
}

// --- two-pane table ------------------------------------------------------

interface TableProps {
  columnConfig: HomesColumnConfig;
  colActions: HomesColumnActions;
  rows: HomeRow[];
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  selectedKey: string | null;
  onSelect: (row: HomeRow) => void;
  onOpen: (row: HomeRow) => void;
  isLoading: boolean;
  error: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  developmentOnly: boolean;
}

function HomesTable(props: TableProps) {
  const { columnConfig, colActions, rows, sortKey, sortDir, onSort, selectedKey } = props;
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const leftBodyContentRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => rightScrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  // Right scroll → translate the frozen left body so the panes stay aligned.
  useEffect(() => {
    const r = rightScrollRef.current;
    if (!r) return;
    const onScroll = () => {
      if (leftBodyContentRef.current) {
        leftBodyContentRef.current.style.transform = `translateY(-${r.scrollTop}px)`;
      }
    };
    onScroll();
    r.addEventListener('scroll', onScroll, { passive: true });
    return () => r.removeEventListener('scroll', onScroll);
  }, [rows.length]);

  useEffect(() => {
    if (!selectedKey) return;
    const i = rows.findIndex((r) => r.home_key === selectedKey);
    if (i >= 0) virtualizer.scrollToIndex(i, { align: 'auto' });
  }, [selectedKey, rows, virtualizer]);

  useEffect(() => {
    if (!props.hasNextPage) return;
    const sentinel = loadMoreSentinelRef.current;
    const scrollRoot = rightScrollRef.current;
    if (!sentinel || !scrollRoot) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !props.isFetchingNextPage) props.onLoadMore();
        }
      },
      { root: scrollRoot, rootMargin: '200px 0px 0px 0px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [props.hasNextPage, props.isFetchingNextPage, props.onLoadMore, rows.length]);

  if (props.isLoading) return <Note>Loading homes…</Note>;
  if (props.error) return <Note tone="error">Could not load homes.</Note>;
  if (rows.length === 0)
    return (
      <Note>
        {props.developmentOnly
          ? 'No homes in development yet — planning-pipeline homes join the index later.'
          : 'No homes match these filters.'}
      </Note>
    );

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const visibleCols = columnConfig.columns.filter((c) => c.visible);
  const rowGridTemplate = visibleCols.map((c) => `${c.width}px`).join(' ');
  const rightMinWidth = visibleCols.reduce((sum, c) => sum + c.width, 0);

  const rowBg = (key: string) =>
    key === selectedKey
      ? 'var(--color-accent-100)'
      : key === hoveredKey
        ? 'var(--hover-subtle)'
        : 'transparent';

  return (
    <div
      onMouseLeave={() => setHoveredKey(null)}
      style={{
        display: 'grid',
        gridTemplateColumns: `${columnConfig.nameWidth}px 1fr`,
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Frozen left pane */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--surface)',
          borderRight: '1px solid var(--hairline)',
          boxShadow: '4px 0 6px -4px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <NameHeader
          onClick={() => onSort('name')}
          isActive={sortKey === 'name'}
          dir={sortDir}
          width={columnConfig.nameWidth}
          onResize={colActions.setNameWidth}
        />
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <div
            ref={leftBodyContentRef}
            style={{ height: totalSize, position: 'relative', willChange: 'transform' }}
          >
            {virtualItems.map((vi) => {
              const row = rows[vi.index]!;
              const name = typeof row['name'] === 'string' ? row['name'] : null;
              return (
                <HomeRowContextMenu key={vi.key} home={targetOf(row)}>
                  <div
                    onMouseEnter={() => setHoveredKey(row.home_key)}
                    onClick={() => props.onSelect(row)}
                    onDoubleClick={() => props.onOpen(row)}
                    title={name ?? undefined}
                    style={{
                      position: 'absolute',
                      top: vi.start,
                      left: 0,
                      right: 0,
                      height: vi.size,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--s-2)',
                      padding: '0 var(--s-3)',
                      background: rowBg(row.home_key),
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      fontSize: 'var(--t-body)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <strong
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}
                    >
                      {name ?? '—'}
                    </strong>
                    {row['lifecycle'] === 'development' && (
                      <span
                        style={{
                          flexShrink: 0,
                          fontSize: 'var(--t-micro, 10px)',
                          color: 'var(--text-secondary)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 3,
                          padding: '0 4px',
                          lineHeight: '14px',
                        }}
                      >
                        In development
                      </span>
                    )}
                  </div>
                </HomeRowContextMenu>
              );
            })}
          </div>
        </div>
      </div>

      {/* Scrollable right pane */}
      <div ref={rightScrollRef} style={{ overflow: 'auto', position: 'relative' }}>
        <div style={{ minWidth: rightMinWidth, position: 'relative' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: rowGridTemplate,
              height: 22,
              borderBottom: '2px solid var(--text-primary)',
              background: 'var(--surface-elevated)',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            {visibleCols.map((col) => (
              <ColumnHeaderCell
                key={col.id}
                col={col}
                columns={columnConfig.columns}
                label={HOMES_COLUMN_DEFS[col.id].label}
                numeric={HOMES_COLUMN_DEFS[col.id].numeric}
                active={sortKey === col.id}
                sortDir={sortDir}
                onSort={onSort}
                moveColumn={colActions.moveColumn}
                setColumnWidth={colActions.setColumnWidth}
                tooltip={HOMES_COLUMN_DEFS[col.id].tooltip}
              />
            ))}
          </div>

          <div style={{ height: totalSize, position: 'relative' }}>
            {virtualItems.map((vi) => {
              const row = rows[vi.index]!;
              return (
                <HomeRowContextMenu key={vi.key} home={targetOf(row)}>
                  <div
                    onClick={() => props.onSelect(row)}
                    onDoubleClick={() => props.onOpen(row)}
                    onMouseEnter={() => setHoveredKey(row.home_key)}
                    aria-selected={row.home_key === selectedKey}
                    style={{
                      position: 'absolute',
                      top: vi.start,
                      left: 0,
                      right: 0,
                      height: vi.size,
                      display: 'grid',
                      gridTemplateColumns: rowGridTemplate,
                      fontSize: 'var(--t-body)',
                      borderBottom: '1px solid var(--border-subtle)',
                      background: rowBg(row.home_key),
                      color: 'var(--text-primary)',
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    {visibleCols.map((col) => {
                      const def = HOMES_COLUMN_DEFS[col.id];
                      const cell = renderHomeCell(row, col.id);
                      return (
                        <div
                          key={col.id}
                          data-numeric={def.numeric || undefined}
                          title={cell.title}
                          style={{
                            padding: '0 var(--s-2)',
                            textAlign: def.numeric ? 'right' : 'left',
                            fontFamily: def.numeric ? 'var(--font-mono)' : 'inherit',
                            fontVariantNumeric: def.numeric ? 'tabular-nums' : undefined,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cell.display}
                        </div>
                      );
                    })}
                  </div>
                </HomeRowContextMenu>
              );
            })}
          </div>

          {(props.hasNextPage || props.isFetchingNextPage) && (
            <div
              ref={loadMoreSentinelRef}
              style={{
                padding: 'var(--s-3)',
                fontSize: 'var(--t-meta)',
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              {props.isFetchingNextPage ? 'Loading more…' : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Note({ children, tone }: { children: React.ReactNode; tone?: 'error' }) {
  return (
    <div
      style={{
        padding: 'var(--s-4)',
        color: tone === 'error' ? 'var(--color-negative-500)' : 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}

/** Frozen Name header: sort button + a resize handle on its right edge. */
function NameHeader({
  onClick,
  isActive,
  dir,
  width,
  onResize,
}: {
  onClick: () => void;
  isActive: boolean;
  dir: 'asc' | 'desc';
  width: number;
  onResize: (px: number) => void;
}) {
  function onResizeDown(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent) => onResize(startW + (ev.clientX - startX));
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }
  return (
    <div style={{ position: 'relative', width }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 4,
          height: 22,
          width: '100%',
          padding: '0 var(--s-3)',
          background: 'var(--surface-elevated)',
          border: 'none',
          borderBottom: '2px solid var(--text-primary)',
          cursor: 'pointer',
          fontSize: 'var(--t-meta)',
          fontWeight: isActive ? 700 : 500,
          color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        <span>Home</span>
        {isActive && (dir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </button>
      <span
        role="separator"
        aria-orientation="vertical"
        onMouseDown={onResizeDown}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 5,
          height: 22,
          cursor: 'col-resize',
        }}
      />
    </div>
  );
}
